import { EditPromptTemplate } from './types';

// Enhanced AI editing prompts with multi-level editing and style guide support
export const aiEditPrompts: EditPromptTemplate = {
  systemPrompt: `You are a professional editor with expertise in multiple writing styles and document types. Your editing approach should be comprehensive and context-aware.

EDITING PRINCIPLES:
- Preserve the author's voice, intent, and key messages
- Apply appropriate style guide conventions (AP, Chicago, MLA, APA, etc.)
- Consider document type, audience, and communication goals
- Provide graduated editing levels based on user needs
- Maintain consistency in tone, style, and terminology

EDITING CATEGORIES:
1. STRUCTURAL: Organization, logical flow, paragraph transitions, information hierarchy
2. CLARITY: Sentence structure, word choice, conciseness, readability optimization
3. STYLE: Tone consistency, voice development, audience appropriateness, brand alignment
4. TECHNICAL: Grammar, punctuation, spelling, citation format, style guide compliance
5. IMPACT: Persuasiveness, engagement, call-to-action effectiveness, message strength

DOCUMENT TYPE EXPERTISE:
- Business documents: Reports, proposals, memos, presentations
- Academic writing: Research papers, essays, dissertations, abstracts
- Marketing content: Copy, web content, social media, advertisements
- Technical documentation: Manuals, specifications, procedures, guides
- Creative writing: Articles, blogs, narratives, creative pieces`,

  editRequestTemplate: (content: string, instructions: string, tone?: string, surroundingContext?: string, editingLevel?: 'light' | 'medium' | 'heavy') => {
    const toneSection = tone
      ? `
    TARGET TONE: ${tone}
    Apply a ${tone.toLowerCase()} tone throughout the editing.`
      : '';

    const intensitySection = editingLevel
      ? `
    EDITING INTENSITY: ${editingLevel.toUpperCase()}
    ${editingLevel === 'light' ? 'Focus on minor corrections and preserve the original voice.' : editingLevel === 'medium' ? 'Improve flow and structure while maintaining the core message.' : 'Feel free to significantly rewrite and restructure for maximum impact.'}`
      : '';

    const contextSection = surroundingContext
      ? `
    DOCUMENT CONTEXT (surrounding text for reference — do NOT edit this, use it only to maintain consistency):
    ---
    ${surroundingContext}
    ---

    IMPORTANT: Use the document context above to maintain consistency in tone, terminology, style, and voice. The edited content should read naturally as part of this document.
    `
      : '';

    return `
    EDITING REQUEST:

    User Instructions: ${instructions}
    ${contextSection}
    ORIGINAL CONTENT (edit this):
    ---
    ${content}
    ---

    EDITING GUIDELINES:
    - Preserve the author's core message and intent
    - Apply the requested improvements systematically
    - Enhance clarity, flow, and impact
    - Maintain consistency in tone and style with the surrounding document
    - Use precise, engaging language appropriate for the audience
    - Ensure proper grammar, punctuation, and style compliance
    ${toneSection}
    ${intensitySection}

    Return the result as a raw JSON object (no markdown formatting or code blocks) with the following structure:
    { "label": "${tone ? tone : 'Improved'}", "content": "the fully edited text..." }
  `;
  },

  suggestionTemplate: (original: string, suggestion: string) => `
    Original: "${original}"
    Suggested: "${suggestion}"

    Changes made to improve:
    - Clarity and readability
    - Sentence structure
    - Word choice
    - Overall impact

    The changes maintain the original meaning while enhancing the delivery.
  `,

  multiLevelEditTemplate: (content: string, instructions: string, editingLevel: 'light' | 'medium' | 'heavy', documentType?: string) => `
    MULTI-LEVEL EDITING REQUEST:

    Editing Level: ${editingLevel.toUpperCase()}
    Document Type: ${documentType || 'General document'}
    User Instructions: ${instructions}

    ORIGINAL CONTENT:
    ---
    ${content}
    ---

    EDITING GUIDELINES FOR ${editingLevel.toUpperCase()} EDIT:
    ${editingLevel === 'light' ?
      `- Fix obvious grammatical and spelling errors
      - Minor clarity improvements
      - Preserve original style and structure
      - Minimal changes to maintain author's voice` :
      editingLevel === 'medium' ?
        `- Improve clarity and readability significantly
      - Enhance word choice and sentence structure
      - Moderate restructuring for better flow
      - Style consistency improvements
      - Strengthen weak passages` :
        `- Comprehensive restructuring for optimal impact
      - Significant style and tone improvements
      - Enhanced readability and engagement
      - Professional polish and refinement
      - Strategic content organization`
    }

    Please provide the edited version that meets the specified editing level requirements.
    Return the result as a raw JSON object (no markdown formatting or code blocks) with the following structure:
    { "label": "${editingLevel} Edit", "content": "the fully edited text..." }
  `,

  styleGuideTemplate: (content: string, styleGuide: string) => `
    STYLE GUIDE EDITING REQUEST:

    Target Style Guide: ${styleGuide}

    CONTENT TO EDIT:
    ---
    ${content}
    ---

    Please edit this content to comply with ${styleGuide} style guide requirements:

    Focus on ${styleGuide}-specific elements:
    - Citation format and reference style
    - Formatting conventions and structure
    - Terminology and language usage standards
    - Punctuation and capitalization rules
    - Number and date formatting
    - Abbreviation and acronym handling

    Ensure the edited content fully adheres to ${styleGuide} standards while maintaining clarity and readability.
    Return the result as a raw JSON object (no markdown formatting or code blocks) with the following structure:
    { "label": "${styleGuide} Style", "content": "the fully edited text..." }
  `,

  // F-08: Template to explain changes between original and suggested text
  explainChangesTemplate: (original: string, suggested: string) => `
    CHANGE EXPLANATION REQUEST:

    Compare the following two versions of text and explain each meaningful change.

    ORIGINAL TEXT:
    ---
    ${original}
    ---

    EDITED TEXT:
    ---
    ${suggested}
    ---

    For each change, explain:
    1. What was changed (briefly quote the before and after)
    2. Why it was changed (grammar rule, clarity improvement, tone adjustment, etc.)

    Return the result as a raw JSON object (no markdown formatting or code blocks) with the following structure:
    {
      "explanations": [
        { "change": "Brief description of what changed", "reason": "Why this change was made" }
      ]
    }

    Be concise. Focus on the most important changes. Group very minor changes (e.g., punctuation fixes) together.
  `
};